import React, { lazy, Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import useTerminalAccess from '../../hooks/useTerminalAccess';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import { canShowTerminal, sandboxOwnerLabel } from '../../services/TerminalService';
import './Terminal.css';

// xterm.js only loads once someone opens the terminal: it stays out of the main bundle.
const TerminalPanel = lazy(() => import('./TerminalPanel'));

/**
 * The Code view's bottom panel with the project's Terminal tab. Shown only when the terminal endpoint allows it for
 * this person (services/TerminalService.js canShowTerminal: never a viewer, never with the owner's plan off, never
 * without an answer), nor where the sandbox admin hid the Terminal tab in the person's plan (Sandbox admin › Plans ›
 * "Tabs people on this plan see": the dock is the same terminal); `enabled` false (a read-only view) does not even ask. The shell starts on the first open and
 * keeps running while the panel is collapsed; it ends when the Code view goes away. A plan hiding Code takes the Code
 * view, and so this dock, away (Canvas's Code gate); the Terminal tab stays as the plan says.
 */
export default function CodeTerminalDock({ enabled = true, projectId }) {
  // The plan's hidden tabs (not the ones a person hides for themselves: those only tidy the tab row).
  const planShowsTerminal = usePlanTab()('terminal');
  const info = useTerminalAccess(projectId, enabled && planShowsTerminal);
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);

  if (!enabled || !planShowsTerminal || !canShowTerminal(info)) { return null; }

  const owner = sandboxOwnerLabel(info);
  const toggle = () => {
    setOpen((value) => !value);
    setStarted(true);
  };

  return (
    <section aria-label="Terminal" className={`code-terminal-dock${open ? ' is-open' : ''}`} data-testid="code-terminal-dock">
      <div className="code-terminal-header">
        <div className="code-terminal-tabs" role="tablist">
          <button
            aria-controls="code-terminal-body"
            aria-selected={open}
            className={`code-terminal-tab${open ? ' active' : ''}`}
            id="code-terminal-tab"
            onClick={toggle}
            role="tab"
            type="button"
          >
            <Icon name="terminal" size={14} />
            <span>{'Terminal'}</span>
          </button>
        </div>
        {owner && <span className="code-terminal-owner" title="Commands run in the project owner's sandbox, with its limits">{owner}</span>}
        <button
          aria-label={open ? 'Collapse the terminal' : 'Expand the terminal'}
          className="code-terminal-toggle"
          onClick={toggle}
          title={open ? 'Collapse' : 'Expand'}
          type="button"
        >
          <Icon name={open ? 'chevron-down' : 'chevron-up'} size={14} />
        </button>
      </div>
      {started && (
        <div aria-labelledby="code-terminal-tab" className="code-terminal-body" hidden={!open} id="code-terminal-body" role="tabpanel">
          {info.available ? (
            <Suspense fallback={<div className="flow-terminal-connecting" role="status">{'Loading the terminal…'}</div>}>
              <TerminalPanel active={open} idleMinutes={info.idleMinutes ?? null} projectId={projectId} />
            </Suspense>
          ) : (
            <div className="code-terminal-unavailable" role="status">{info.message || 'The terminal is not available for this project.'}</div>
          )}
        </div>
      )}
    </section>
  );
}

CodeTerminalDock.propTypes = {
  // false in a read-only view: no tab, no request
  enabled: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
};
